import React, { useState, useEffect } from 'react';
import { Save, X } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const RoadmapSessionModal = ({ showModal, onClose, onSubmit, initialData = {}, isEditMode, selectedLevelName }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('Technical');
  const [order, setOrder] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [estimatedHours, setEstimatedHours] = useState('');

  useEffect(() => {
    if (showModal) {
      if (isEditMode && initialData) {
        setTitle(initialData.title || '');
        setDescription(initialData.description || '');
        setType(initialData.type || 'Technical');
        setOrder(initialData.order?.toString() || '');
        setDueDate(initialData.dueDate || '');
        setEstimatedHours(initialData.estimatedHours?.toString() || '');
      } else {
        setTitle('');
        setDescription('');
        setType('Technical');
        setOrder(initialData.order !== undefined ? initialData.order.toString() : '');
        setDueDate('');
        setEstimatedHours('');
      }
    }
  }, [showModal, isEditMode, initialData]);

  const handleSubmit = () => {
    const submitData = {
      title,
      description,
      type,
      order: parseInt(order) || 1,
      dueDate: dueDate || null,
      estimatedHours: estimatedHours ? parseInt(estimatedHours) : null,
    };
    
    onSubmit(submitData);
    onClose();
  };

  // React Quill modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ]
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline',
    'list', 'bullet'
  ];

  if (!showModal) return null;

  return (
    <>
      <style>{`
        .quill-modal .ql-toolbar {
          background-color: #334155;
          border: 1px solid #475569;
          border-top-left-radius: 0.375rem;
          border-top-right-radius: 0.375rem;
        }
        
        .quill-modal .ql-container {
          background-color: #334155;
          border: 1px solid #475569;
          border-top: none;
          border-bottom-left-radius: 0.375rem;
          border-bottom-right-radius: 0.375rem;
          min-height: 120px;
        }
        
        .quill-modal .ql-editor {
          color: white;
          min-height: 120px;
        }
        
        .quill-modal .ql-editor.ql-blank::before {
          color: #94a3b8;
        }
        
        .quill-modal .ql-stroke {
          stroke: #94a3b8;
        }
        
        .quill-modal .ql-fill {
          fill: #94a3b8;
        }
        
        .quill-modal .ql-picker-label {
          color: #94a3b8;
        }
        
        .quill-modal .ql-toolbar button:hover .ql-stroke,
        .quill-modal .ql-toolbar button:focus .ql-stroke,
        .quill-modal .ql-toolbar button.ql-active .ql-stroke {
          stroke: #3b82f6;
        }
        
        .quill-modal .ql-toolbar button:hover .ql-fill,
        .quill-modal .ql-toolbar button:focus .ql-fill,
        .quill-modal .ql-toolbar button.ql-active .ql-fill {
          fill: #3b82f6;
        }
      `}</style>
      
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-slate-800 rounded-xl max-w-xl w-full p-6 text-white border border-slate-700 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">{isEditMode ? 'Edit Session' : `Add New Session to ${selectedLevelName}`}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-700 rounded-lg"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-start block text-sm font-medium text-slate-300 mb-1">Session Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md text-white"
                required
              />
            </div>
            
            <div>
              <label className="text-start block text-sm font-medium text-slate-300 mb-1">Description</label>
              <div className="quill-modal">
                <ReactQuill
                  theme="snow"
                  value={description}
                  onChange={setDescription}
                  modules={modules}
                  formats={formats}
                  placeholder="Enter session description..."
                />
              </div>
            </div>
            
            <div>
              <label className="text-start block text-sm font-medium text-slate-300 mb-1">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md text-white"
              >
                <option value="Technical">Technical</option>
                <option value="Practical">Practical</option>
                <option value="Theory">Theory</option>
                <option value="Project">Project</option>
              </select>
            </div>
            
            <div>
              <label className="text-start block text-sm font-medium text-slate-300 mb-1">Order</label>
              <input
                type="number"
                value={order}
                onChange={(e) => setOrder(e.target.value)}
                className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md text-white"
                min="1"
                required
              />
            </div>
            
            <div>
              <label className="text-start block text-sm font-medium text-slate-300 mb-1">Due Date (Optional)</label>
              <input
                type="text"
                placeholder="e.g., Dec 31, 2024"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md text-white"
              />
            </div>
            
            <div>
              <label className="text-start block text-sm font-medium text-slate-300 mb-1">Estimated Hours (Optional)</label>
              <input
                type="number"
                value={estimatedHours}
                onChange={(e) => setEstimatedHours(e.target.value)}
                className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md text-white"
                min="1"
              />
            </div>

            <button
              onClick={handleSubmit}
              className="w-full py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium flex items-center justify-center gap-2"
              disabled={!title || !order}
            >
              <Save className="w-5 h-5" /> {isEditMode ? 'Save Changes' : 'Add Session'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default RoadmapSessionModal;